import { useState, useRef, useEffect } from 'react';
import { useLocale } from './context.tsx';
import { LOCALE_NATIVE_LABELS, SUPPORTED_LOCALES } from './types.ts';

export function LocaleSwitcher(): JSX.Element {
  const { locale, setLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative" data-testid="locale-switcher">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[13px] text-ink-subtle hover:text-ink transition-colors"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        {LOCALE_NATIVE_LABELS[locale]}
        <svg className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 4.5 6 7.5 9 4.5" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 min-w-[120px] rounded-xl bg-linen shadow-warm-md ring-1 ring-ink/5 py-1 z-50">
          {SUPPORTED_LOCALES.map((target) => (
            <button
              key={target}
              type="button"
              data-testid={`locale-switcher-${target}`}
              onClick={() => { setLocale(target); setOpen(false); }}
              className={`w-full px-4 py-2 text-left text-[13px] transition-colors ${
                locale === target
                  ? 'font-semibold text-claude'
                  : 'text-ink-subtle hover:text-ink hover:bg-butter/50'
              }`}
            >
              {LOCALE_NATIVE_LABELS[target]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
